import React from "react";
import { MapPin, CalendarDays, Clock, Gift, CheckCircle2, MonitorPlay } from "lucide-react";

export const fmtDia = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "");
export const isPresencial = (c) => c?.modality === "presencial";
const hojeISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
/** curso presencial cujo último dia já passou */
export const presencialRealizado = (c) => isPresencial(c) && !!(c.event_end || c.event_date) && (c.event_end || c.event_date) < hojeISO();
/** situação do curso para o admin: realizado (presencial que já aconteceu) / publicado / rascunho */
export const situacaoCurso = (c) => (c?.published === false ? "rascunho" : presencialRealizado(c) ? "realizado" : c?.published ? "publicado" : "rascunho");
export function SituacaoBadge({ c, className = "" }) {
  const s = situacaoCurso(c);
  const cls = s === "realizado" ? "text-sky-800 bg-sky-50 border-sky-200" : s === "publicado" ? "text-green-700 bg-green-50 border-green-200" : "text-slate-600 bg-slate-50 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border whitespace-nowrap ${cls} ${className}`} title={s === "realizado" ? "Curso presencial já aconteceu: fica como histórico para as alunas e sai da vitrine" : undefined}>
      {s === "realizado" && <CheckCircle2 className="h-3.5 w-3.5" />}{s === "realizado" ? "Realizado" : s === "publicado" ? "Publicado" : "Rascunho"}
    </span>
  );
}
/** 09/11/2026 · 09 e 10/11/2026 · 09 a 12/11/2026 · 30/11 a 01/12/2026 */
export function periodo(ini, fim) {
  if (!ini) return "";
  if (!fim || fim === ini) return fmtDia(ini);
  const n = Math.round((new Date(fim) - new Date(ini)) / 86400000);
  const sep = n === 1 ? "e" : "a";
  if (ini.slice(0, 7) === fim.slice(0, 7)) return `${ini.slice(8, 10)} ${sep} ${fmtDia(fim)}`;
  if (ini.slice(0, 4) === fim.slice(0, 4)) return `${ini.slice(8, 10)}/${ini.slice(5, 7)} ${sep} ${fmtDia(fim)}`;
  return `${fmtDia(ini)} a ${fmtDia(fim)}`;
}

/** Etiqueta curta para os cartões de curso: "📍 Presencial · Recife/PE · 15/11/2026". */
export function PresencialTag({ c, className = "" }) {
  if (!isPresencial(c)) return null;
  const local = [c.city, c.state].filter(Boolean).join("/");
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold text-[#B45309] ${className}`} data-testid="presencial-tag">
      <MapPin className="h-3.5 w-3.5" /> Presencial{local ? ` · ${local}` : ""}{c.event_date ? ` · ${periodo(c.event_date, c.event_end)}` : ""}
    </span>
  );
}

/** Quadro com data, horário e local da turma presencial (área do aluno e página de venda). */
export function PresencialInfo({ c, bonus = [], seatsLeft = null }) {
  const dias = periodo(c.event_date, c.event_end);
  const local = [c.city, c.state].filter(Boolean).join("/");
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 space-y-3" data-testid="presencial-info">
      <div className="text-xs font-bold uppercase tracking-wider text-[#B45309]">Curso presencial</div>
      {dias && <div className="flex items-start gap-2 text-slate-800"><CalendarDays className="h-5 w-5 text-[#B45309] shrink-0" /> <span><strong>{dias}</strong>{c.event_time ? ` · ${c.event_time}` : ""}</span></div>}
      {!dias && c.event_time && <div className="flex items-start gap-2 text-slate-800"><Clock className="h-5 w-5 text-[#B45309] shrink-0" /> {c.event_time}</div>}
      {(c.venue || c.address || local) && (
        <div className="flex items-start gap-2 text-slate-800"><MapPin className="h-5 w-5 text-[#B45309] shrink-0" />
          <span>{c.venue && <strong>{c.venue}</strong>}{c.venue && (c.address || local) ? " — " : ""}{[c.address, local].filter(Boolean).join(", ")}</span></div>
      )}
      {bonus.length > 0 && (
        <div className="flex items-start gap-2 text-slate-800"><Gift className="h-5 w-5 text-[#1E9E2A] shrink-0" />
          <span>Bônus incluso: <strong>{bonus.join(" + ")}</strong> (curso online)</span></div>
      )}
      {c.program && (
        <div className="pt-3 border-t border-amber-200" data-testid="presencial-program">
          <div className="text-sm font-bold text-slate-800 mb-2">Grade do curso</div>
          <ProgramView text={c.program} />
        </div>
      )}
      {seatsLeft !== null && <div className={`text-sm font-semibold ${seatsLeft > 0 ? "text-slate-700" : "text-red-600"}`}>{seatsLeft > 0 ? `${seatsLeft} vaga(s) disponível(is)` : "Vagas esgotadas"}</div>}
    </div>
  );
}

/**
 * Grade do curso com formatação simples (o admin digita texto):
 *   # 1º DIA — título        -> faixa do dia
 *   08h30 · Credenciamento   -> horário
 *   - tópico                 -> item com ✓
 *   Prática: item · item     -> rótulo + etiquetas
 *   outra linha              -> texto
 */
export function ProgramView({ text }) {
  const linhas = (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  return (
    <div className="space-y-1.5 text-sm text-slate-700">
      {linhas.map((l, i) => {
        if (l.startsWith("#")) return (
          <div key={i} className="mt-3 first:mt-0 rounded-lg px-3 py-1.5 font-bold text-white text-[13px] tracking-wide" style={{ background: "linear-gradient(90deg,#232D4F,#1E9E2A)" }}>{l.replace(/^#+\s*/, "")}</div>
        );
        if (/^[-•✓]\s*/.test(l)) return (
          <div key={i} className="flex items-start gap-2 pl-1"><CheckCircle2 className="h-4 w-4 text-[#1E9E2A] shrink-0 mt-0.5" /><span>{l.replace(/^[-•✓]\s*/, "")}</span></div>
        );
        const hora = l.match(/^(\d{1,2}h\d{0,2})\s*[·—–-]\s*(.+)$/);
        if (hora) return (
          <div key={i} className="flex items-center gap-2"><span className="inline-flex items-center gap-1 rounded-md bg-amber-100 text-[#B45309] font-bold text-xs px-2 py-0.5"><Clock className="h-3 w-3" />{hora[1]}</span><span className="font-medium text-slate-800">{hora[2]}</span></div>
        );
        const rot = l.match(/^([^:]{3,40}):\s*(.+·.+)$/);
        if (rot) return (
          <div key={i} className="pt-1"><div className="text-xs font-bold uppercase tracking-wide text-[#232D4F] mb-1">{rot[1]}</div>
            <div className="flex flex-wrap gap-1.5">{rot[2].split("·").map((x) => x.trim()).filter(Boolean).map((x) => <span key={x} className="rounded-full bg-green-50 border border-green-200 text-green-800 text-xs font-medium px-2.5 py-0.5">{x}</span>)}</div></div>
        );
        return <p key={i} className="text-slate-600">{l}</p>;
      })}
    </div>
  );
}

/** Abas "Cursos online" / "Cursos presenciais" (links de venda). Devolve a aba escolhida e a lista filtrada. */
export function useModalityTab(list, key = "iep-links-aba", extras = []) {   // extras: [[id, rótulo, Ícone, quantidade]]
  const [tab, setTab] = React.useState(() => { try { return localStorage.getItem(key) || "online"; } catch (e) { return "online"; } });
  const escolher = (t) => { setTab(t); try { localStorage.setItem(key, t); } catch (e) {} };
  const online = (list || []).filter((c) => !isPresencial(c));
  const pres = (list || []).filter(isPresencial);
  const atual = tab === "presencial" ? pres : tab === "online" ? online : [];
  const tabs = (
    <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1" data-testid="modality-tabs">
      {[["online", "Cursos online", MonitorPlay, online.length], ["presencial", "Cursos presenciais", MapPin, pres.length], ...extras].map(([id, label, Icon, n]) => (
        <button key={id} type="button" onClick={() => escolher(id)} data-testid={`tab-${id}`}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === id ? "bg-white shadow text-[#232D4F]" : "text-slate-500 hover:text-slate-700"}`}>
          <Icon className="h-4 w-4" /> {label} {n !== null && n !== undefined && <span className={`text-xs px-1.5 rounded-full ${tab === id ? "bg-slate-100" : "bg-slate-200"}`}>{n}</span>}
        </button>
      ))}
    </div>
  );
  return { tab, tabs, list: atual };
}
